/**
 * Maps permissions to their corresponding routes
 * Routes are ordered by priority - first match will be used
 */
export const permissionRouteMap: Array<{ permission: string; route: string }> = [
  // Tracking/Dashboard
  { permission: 'tracking.view', route: '/dashboard' },
  { permission: 'tracking.livemap', route: '/dashboard' },
  { permission: 'tracking.lifecycle', route: '/dashboard' },
  
  // Management routes (ordered by common usage)
  { permission: 'ambulance.list', route: '/dashboard/ambulances' },
  { permission: 'ambulance.get', route: '/dashboard/ambulances' },
  { permission: 'booking.list', route: '/dashboard/bookings' },
  { permission: 'booking.get', route: '/dashboard/bookings' },
  { permission: 'patient.list', route: '/dashboard/patients' },
  { permission: 'patient.get', route: '/dashboard/patients' },
  { permission: 'user.list', route: '/dashboard/users' },
  { permission: 'user.get', route: '/dashboard/users' },
  { permission: 'attendance.list', route: '/dashboard/attendance' },
  { permission: 'report.read', route: '/dashboard/reports' },
  
  // Settings routes
  { permission: 'ambulancetype.list', route: '/dashboard/ambulance-types' },
  { permission: 'ambulancetype.get', route: '/dashboard/ambulance-types' },
  { permission: 'gpsdevice.list', route: '/dashboard/gps-devices' },
  { permission: 'gpsdevice.get', route: '/dashboard/gps-devices' },
  { permission: 'area.list', route: '/dashboard/areas' },
  { permission: 'area.get', route: '/dashboard/areas' },
  { permission: 'permission.list', route: '/dashboard/permissions' },
  { permission: 'permission.get', route: '/dashboard/permissions' },
  { permission: 'role.list', route: '/dashboard/roles' },
  { permission: 'role.get', route: '/dashboard/roles' },
  { permission: 'staffcategory.list', route: '/dashboard/staff-categories' },
  { permission: 'staffcategory.get', route: '/dashboard/staff-categories' },
  { permission: 'patientvitalfield.get', route: '/dashboard/patient-vital-fields' },
  { permission: 'patientvitalfield.list', route: '/dashboard/patient-vital-fields' },
  { permission: 'tenant.update', route: '/dashboard/tenant-settings' },
  { permission: 'tenant.list', route: '/dashboard/tenants' },
  { permission: 'profile.view', route: '/dashboard/profile' },
];

/**
 * Finds the first available route based on user permissions
 * Note: This function is only for normal users, not for system admins or tenant admins
 * @param hasPermission - Function to check if user has a permission
 * @param isSystemAdmin - Whether user is system admin (should always redirect to /dashboard/tenants)
 * @param isTenantAdmin - Whether user is tenant admin (should always redirect to /dashboard)
 * @returns The first available route, or '/dashboard' as fallback
 */
export const getFirstAvailableRoute = (
  hasPermission: (permission: string) => boolean,
  isSystemAdmin: boolean,
  isTenantAdmin: boolean
): string => {
  // System admins always go to tenants page
  if (isSystemAdmin) {
    return '/dashboard/tenants';
  }
  
  // Tenant admins always go to dashboard
  if (isTenantAdmin) {
    return '/dashboard';
  }
  
  // For normal users, check permissions in order and return first match
  for (const { permission, route } of permissionRouteMap) {
    if (hasPermission(permission)) {
      return route;
    }
  }
  
  // Fallback to dashboard (which requires tracking.view)
  // If user doesn't have tracking.view, ProtectedRoute will handle it
  return '/dashboard';
};

